import { ChangeEvent, SyntheticEvent, useContext, useState } from "react"
import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { v4 as uuid } from "uuid"
import { validateAddress, validateUser } from "./validate"
import { Address, AddressSearch } from "../../../api/types/address"
import { Account } from "../../../api/types/account"
import { useAuthRegistration } from "../../../api/queries/useAuthRegistration"
import { useNavigate } from "react-router-dom"
import { ConstantsResponseData } from "../../../api/types/constants"
import { useConstants } from "../../../api/queries/useConstants"
import { useAddressSearch } from "../../../api/queries/useAddressSearch"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import { classNames } from "primereact/utils"
import commonStyles from "../../common.module.css"
import FieldError, { FormErrors } from "../../FieldError"
import { PhoneInput } from "../../common/PhoneInput"
import { AuthRegistrationPayload, RegisterUserPayload } from "../../../api/types/auth"
import { User } from "../../../api/types/user"
import { ResponseDataError } from "../../../api/types/base"
import globalToastContext from "../../../context/globalToast"
import { countryItemTemplate, countryValueTemplate } from "../../common/country"

export default function RegistrationForm() {
  const navigate = useNavigate()
  const authRegistration = useAuthRegistration()
  const { ref } = useContext(globalToastContext)

  const [searchIC, setSearchIC] = useState<string>("")
  const [lockedFields, setLockedFields] = useState<(keyof Address)[]>([])
  const isLocked = (name: keyof Address): boolean => lockedFields.indexOf(name) > -1

  const { data: constants }: { data: ConstantsResponseData | undefined } = useConstants()
  const { data: addressSearch, isLoading: isAddressLoading } = useAddressSearch(searchIC)

  const handleSearchICChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchIC(event.target.value)
  }

  const fillAddress = (): void => {
    if (addressSearch && addressSearch.results.addressList.length > 0) {
      let newAddress: AddressSearch = addressSearch.results.addressList[0]

      if (newAddress.uuid) {
        setUser({ ...user, billingAddress: newAddress.uuid })
      }

      if (newAddress.country) {
        const prefix = constants?.results.countries
          .find((country) => country.value === newAddress.country)?.prefix

        if (prefix) {
          setUser({ ...user, phonePrefix: prefix, mobilePrefix: prefix })
        }
      }

      const fields: (keyof Address)[] = [
        "bankAccount",
        "bankAccountPrefix",
        "bankCode",
        "city",
        "country",
        "dic",
        "ic",
        "name",
        "street",
        "streetDetails",
        "zip",
      ]

      let newLockedFields: (keyof Address)[] = []

      fields.forEach((field) => {
        if (newAddress[field]) {
          newLockedFields.push(field)
        }
      })

      setLockedFields(newLockedFields)
      setAddress({ ...address, ...newAddress })
    }
  }

  const unLock = () => {
    setLockedFields([])
  }

  const handleBackClick = (): void => navigate("/login")

  const addressUuid = uuid()

  const [address, setAddress] = useState<Address>({
    uuid: addressUuid,
    name: "",
    street: "",
    streetDetails: "",
    zip: "",
    city: "",
    country: "",
    ic: null,
    dic: null,
    bankAccount: null,
    bankAccountPrefix: null,
    bankCode: null,
    // fixed data for main address
    isMain: true,
  })

  const [user, setUser] = useState<Account>({
    uuid: uuid(),
    firstname: "",
    lastname: "",
    email: "",
    phonePrefix: "",
    phone: null,
    mobilePrefix: "",
    mobile: null,
    useAsLoadingDefaultAddress: false,
    billingEmail: "",
    // fixed data for admin
    isAdmin: true,
    canManageUsers: true,
    billingAddress: addressUuid,
  })

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, name: event.target.value })
    resetAddressErrorsFor(["name"])
  }

  const handleStreetChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, street: event.target.value })
    resetAddressErrorsFor(["street"])
  }

  const handleStreetDetailsChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, streetDetails: event.target.value })
  }

  const handleZipChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, zip: event.target.value })
    resetAddressErrorsFor(["zip"])
  }

  const handleCityChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, city: event.target.value })
    resetAddressErrorsFor(["city"])
  }

  const handleCountryChange = (event: DropdownChangeParams): void => {
    setAddress({ ...address, country: event.target.value })
    resetAddressErrorsFor(["country"])

    const prefix = constants?.results.countries
      .find((country) => country.value === event.target.value)?.prefix

    if (prefix) {
      setUser({ ...user, phonePrefix: prefix, mobilePrefix: prefix })
    }
  }

  const handleICChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, ic: event.target.value })
    resetAddressErrorsFor(["ic"])
  }

  const handleDICChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, dic: event.target.value })
    resetAddressErrorsFor(["dic"])
  }

  const handleBankAccountPrefixChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, bankAccountPrefix: event.target.value })
  }

  const handleBankAccountChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, bankAccount: event.target.value })
  }

  const handleBankCodeChange = (event: DropdownChangeParams): void => {
    setAddress({ ...address, bankCode: event.target.value })
  }

  const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUser({ ...user, firstname: event.target.value })
    resetUserErrorsFor(["firstname"])
  }

  const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUser({ ...user, lastname: event.target.value })
    resetUserErrorsFor(["lastname"])
  }

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUser({ ...user, email: event.target.value })
    resetUserErrorsFor(["email"])
  }

  const handleBillingEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUser({ ...user, billingEmail: event.target.value })
  }

  const handleMobileChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setUser({ ...user, mobilePrefix: prefix, mobile: value })
    resetUserErrorsFor(["mobile", "phone"])
  }

  const handlePhoneChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setUser({ ...user, phonePrefix: prefix, phone: value })
    resetUserErrorsFor(["phone", "mobile"])
  }

  // const handleBillingAddressChange = (event: DropdownChangeParams): void => {
  //   setUser({ ...user, billingAddress: event.target.value })
  //   resetUserErrorsFor(["billingAddress"])
  // }

  // const handleCanManageUsersChange = (event: CheckboxChangeParams): void => {
  //   setUser({ ...user, canManageUsers: event.checked })
  // }

  const handleUseAsLoadingDefaultAddress = (event: CheckboxChangeParams): void => {
    setUser({ ...user, useAsLoadingDefaultAddress: event.checked })
  }

  const [addressErrors, setAddressErrors] = useState<FormErrors<Address>>()
  const [userErrors, setUserErrors] = useState<FormErrors<RegisterUserPayload>>()

  const resetAddressErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}
    names.forEach((name) => { obj[name] = "" })
    setAddressErrors(Object.assign({}, addressErrors, obj))
  }

  const resetUserErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}
    names.forEach((name) => { obj[name] = "" })
    setUserErrors(Object.assign({}, userErrors, obj))
  }

  const onSubmit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault()

    let payload: AuthRegistrationPayload = {
      user: {
        uuid: user.uuid,
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        phone: user.phone ? `${user.phonePrefix}${user.phone}` : "",
        mobile: user.mobile ? `${user.mobilePrefix}${user.mobile}` : "",
        billingEmail: user.billingEmail,
        billingAddress: user.billingAddress,
        canManageUsers: user.canManageUsers,
        isAdmin: user.isAdmin,
        useAsLoadingDefaultAddress: user.useAsLoadingDefaultAddress,
      },
      address: address,
    }

    const [isAddressValid, newAddressErrors] = validateAddress(payload.address)
    setAddressErrors(newAddressErrors)

    const [isUserValid, newUserErrors] = validateUser(payload.user)
    setUserErrors(newUserErrors)

    if (isAddressValid && isUserValid) {
      let { errors } = await authRegistration.mutateAsync(payload)

      if (errors.length > 0) {
        let respAddressErrors: FormErrors<Address> = {}
        let respUserErrors: FormErrors<User> = {}

        errors.forEach((error: ResponseDataError) => {
          // workaround
          respAddressErrors[error.property as keyof Address] = error.message
          respUserErrors[error.property as keyof User] = error.message
        })

        setAddressErrors(respAddressErrors)
        setUserErrors(respUserErrors)

        return
      }

      ref?.current?.show({
        severity: "success",
        sticky: true,
        content: (
          <span>
            Žádost o registraci proběhla úspěšně. Na Vámi zadanou emailovou adresu <strong>{user.email}</strong> dorazí bližší instrukce. Pokud nemůžete zprávu najít, zkontrolujte si prosím i nevyžádanou poštu (spam). Případně nás kontaktujte na <a href="mailto:tpal@dopravanapaletach.cz" style={{ color: "#224a23" }}>tpal@dopravanapaletach.cz</a>.
          </span>
        )
      })

      navigate("/login")
    }
  }

  return (
    <form onSubmit={onSubmit} className={"col-12"}>
      <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
        Základní údaje o společnosti
      </div>

      <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
        <div className={"p-fluid"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={searchIC} onChange={handleSearchICChange} maxLength={8} />
                <label>Vyhledat společnost dle IČ:</label>
              </span>
            </div>
            <div className={"field col-12 md:col-8 flex align-items-center"}>
              {isAddressLoading && (
                <i className={"pi pi-spin pi-spinner"} />
              )}
              {addressSearch && addressSearch.errors.length > 0 && (
                <span>{addressSearch.errors.join(", ")}</span>
              )}
              {addressSearch && addressSearch.results.addressList.length > 0 && (
                <>
                  <span>Nalezena společnost {addressSearch.results.addressList[0].name}</span>
                  <span className="flex-1">
                    {lockedFields.length === 0 ? (
                      <Button type={"button"} label={"Předvyplnit"}
                        className={"p-button-sm p-button-secondary w-auto ml-3"}
                        onClick={fillAddress} />
                    ) : (
                      <Button type={"button"} label={"Odemknout pro úpravy"} icon={"pi pi-lock-open"}
                        onClick={unLock} className={"p-button-sm p-button-secondary w-auto ml-3"} />
                    )}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={`p-float-label ${isLocked("name") ? "p-input-icon-right" : ""}`}>
                {isLocked("name") && <i className="pi pi-lock" />}

                <InputText value={address.name} onChange={handleNameChange}
                  className={classNames({ "p-invalid": !!addressErrors?.name })}
                  disabled={isLocked("name")} />
                <label className={commonStyles.labelRequired}>
                  Název společnost:
                </label>
              </span>
              <FieldError error={addressErrors?.name} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={`p-float-label ${isLocked("street") ? "p-input-icon-right" : ""}`}>
                {isLocked("street") && <i className="pi pi-lock" />}

                <InputText value={address.street} onChange={handleStreetChange}
                  className={classNames({ "p-invalid": !!addressErrors?.street })}
                  disabled={isLocked("street")} />
                <label className={commonStyles.labelRequired}>Ulice a číslo domu:</label>
              </span>
              <FieldError error={addressErrors?.street} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={`p-float-label ${isLocked("streetDetails") ? "p-input-icon-right" : ""}`}>
                {isLocked("streetDetails") && <i className="pi pi-lock" />}

                <InputText value={address.streetDetails || ""} onChange={handleStreetDetailsChange}
                  disabled={isLocked("streetDetails")} />
                <label>Upřesnění:</label>
              </span>
              <small className={commonStyles.fieldHelp}>Součást fakturační adresy</small>
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={`p-float-label ${isLocked("zip") ? "p-input-icon-right" : ""}`}>
                {isLocked("zip") && <i className="pi pi-lock" />}

                <InputText value={address.zip} onChange={handleZipChange}
                  className={classNames({ "p-invalid": !!addressErrors?.zip })}
                  disabled={isLocked("zip")} />
                <label className={commonStyles.labelRequired}>PSČ:</label>
              </span>
              <FieldError error={addressErrors?.zip} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={`p-float-label ${isLocked("city") ? "p-input-icon-right" : ""}`}>
                {isLocked("city") && <i className="pi pi-lock" />}

                <InputText value={address.city} onChange={handleCityChange}
                  className={classNames({ "p-invalid": !!addressErrors?.city })}
                  disabled={isLocked("city")} />
                <label className={commonStyles.labelRequired}>Obec:</label>
              </span>
              <FieldError error={addressErrors?.city} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={`p-float-label ${isLocked("country") ? "p-input-icon-right" : ""}`}>
                {isLocked("country") && <i className="pi pi-lock" />}

                <Dropdown options={constants?.results.countries} value={address.country}
                  onChange={handleCountryChange}
                  className={classNames({ "p-invalid": !!addressErrors?.country })}
                  disabled={isLocked("country")}
                  itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate}
                />
                <label className={commonStyles.labelRequired}>Stát:</label>
              </span>
              <FieldError error={addressErrors?.country} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={`p-float-label ${isLocked("ic") ? "p-input-icon-right" : ""}`}>
                {isLocked("ic") && <i className="pi pi-lock" />}

                <InputText value={address.ic || ""} onChange={handleICChange}
                  className={classNames({ "p-invalid": !!addressErrors?.ic })}
                  disabled={isLocked("ic")} />
                <label className={address.country === "CZ" ? commonStyles.labelRequired : null}>IČ:</label>
              </span>
              <FieldError error={addressErrors?.ic} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={`p-float-label ${isLocked("dic") ? "p-input-icon-right" : ""}`}>
                {isLocked("dic") && <i className="pi pi-lock" />}

                <InputText value={address.dic || ""} onChange={handleDICChange}
                  className={classNames({ "p-invalid": !!addressErrors?.dic })}
                  disabled={isLocked("dic")} />
                <label className={commonStyles.labelRequired}>DIČ:</label>
              </span>
              <FieldError error={addressErrors?.dic} />
            </div>

            <div className={"col-12"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={`p-float-label ${isLocked("bankAccountPrefix") ? "p-input-icon-right" : ""}`}>
                    {isLocked("bankAccountPrefix") && <i className="pi pi-lock" />}

                    <InputText value={address.bankAccountPrefix || ""} onChange={handleBankAccountPrefixChange}
                      disabled={isLocked("bankAccountPrefix")} />
                    <label>Předčíslí:</label>
                  </span>
                </div>
                <div className={"field col-12 md:col-4"}>
                  <span className={`p-float-label ${isLocked("bankAccount") ? "p-input-icon-right" : ""}`}>
                    {isLocked("bankAccount") && <i className="pi pi-lock" />}

                    <InputText value={address.bankAccount || ""} onChange={handleBankAccountChange}
                      disabled={isLocked("bankAccount")} />
                    <label>Číslo účtu:</label>
                  </span>
                </div>
                <div className={"field col-12 md:col-4"}>
                  <span className={`p-float-label ${isLocked("bankCode") ? "p-input-icon-right" : ""}`}>
                    {isLocked("bankCode") && <i className="pi pi-lock" />}

                    <Dropdown options={constants?.results.bankCodes} value={address.bankCode || ""}
                      onChange={handleBankCodeChange} filter={true} filterBy={"label"}
                      emptyFilterMessage={"Nic nenalezeno"}
                      disabled={isLocked("bankCode")} />
                    <label>Kód banky:</label>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <div className={"field-checkbox"}>
                <Checkbox name={"isLoadingDefaultAddress"} inputId={"isLoadingDefaultAddress"}
                  checked={user.useAsLoadingDefaultAddress}
                  onChange={handleUseAsLoadingDefaultAddress} />
                <label htmlFor={"isLoadingDefaultAddress"}>Použít jako výchozí adresu nakládky</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
        Hlavní admin účet společnosti
      </div>

      <div className={"border-solid border-1 border-300 px-3 pt-4 mb-6"}>
        <div className={"p-fluid"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={user.firstname} onChange={handleFirstNameChange}
                  className={classNames({ "p-invalid": !!userErrors?.firstname })} />
                <label className={commonStyles.labelRequired}>Jméno:</label>
              </span>
              <FieldError error={userErrors?.firstname} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={user.lastname} onChange={handleLastNameChange}
                  className={classNames({ "p-invalid": !!userErrors?.lastname })} />
                <label className={commonStyles.labelRequired}>Příjmení:</label>
              </span>
              <FieldError error={userErrors?.lastname} />
            </div>

            {/* <div className={"field-checkbox col-6 md:col-4 md:pt-4"}>
              <Checkbox checked={user.canManageUsers} onChange={handleCanManageUsersChange}
                inputId={`can-manage-users-${user.uuid}`} />
              <label htmlFor={`can-manage-users-${user.uuid}`}>může spravovat uživatele</label>
            </div> */}
          </div>

          <div className={"grid"}>
            <div className={"field col-6 md:col-4"}>
              <span className="p-float-label">
                <InputText value={user.email} onChange={handleEmailChange}
                  className={classNames({ "p-invalid": !!userErrors?.email })} />
                <label className={commonStyles.labelRequired}>E-mail:</label>
              </span>
              <FieldError error={userErrors?.email} />
            </div>

            <div className={"field col-6 md:col-4"}>
              <span className="p-float-label">
                <InputText value={user.billingEmail || ""} onChange={handleBillingEmailChange}
                  className={classNames({ "p-invalid": !!userErrors?.billingEmail })} />
                <label className={commonStyles.labelRequired}>E-mail pro fakturaci:</label>
              </span>
              <FieldError error={userErrors?.billingEmail} />
              <small className={commonStyles.fieldHelp}> Více e-mailů oddělte středníkem.</small>
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <PhoneInput placeholder={"Mobil:"} prefix={user.mobilePrefix}
                value={user.mobile} onChange={handleMobileChange}
                isRequired={!user.phone} error={userErrors?.mobile} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <PhoneInput placeholder={"Telefon:"} prefix={user.phonePrefix}
                value={user.phone} onChange={handlePhoneChange}
                isRequired={!user.mobile} error={userErrors?.phone} />
            </div>
          </div>

          <div className={"grid"}>
            {/* <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <Dropdown options={[]} optionValue={"uuid"} optionLabel={"name"}
                  value={user.billingAddress} onChange={handleBillingAddressChange}
                  className={classNames({ "p-invalid": !!userErrors?.billingAddress })} />
                <label className={commonStyles.labelRequired}>Přiřazení k fakturační adrese:</label>
              </span>
              <FieldError error={userErrors?.billingAddress} />
            </div> */}
          </div>
        </div>
      </div>

      <div className={"flex"}>
        <div className={"flex-1"}>
          <Button type={"button"} label={"Zpět"}
            className={"p-button p-button-sm p-button-secondary mr-3"}
            icon={"pi pi-arrow-circle-left"}
            onClick={handleBackClick} />
        </div>

        <div className={"flex-1 text-right"}>
          <Button type={"submit"} label={"Registrovat"} icon={"pi pi-user-plus"}
            className={"p-button p-button-sm p-button-success"} />
        </div>
      </div>
    </form>
  )
}
